import { Language } from 'src/api/language'
import { CityTaxExonerationCode, GenderType, GuestType, ReservedResourcePhase } from 'src/api/api.generated'
import * as z from 'zod'
import { TFunction } from 'i18next'
import { isGuarantor } from '../../../../utils/guest-occupancy'
import { compareDates, todayStartOfTheDay } from 'src/utils/date'

function getGuestAddressSchema(t?: TFunction) {
  return z
    .object({
      countryId: z.string(),
      municipalityId: z.string().nullable(),
      province: z.string().nullable(),
      city: z.string().nullable(),
    })
    .nullable()
}

function getIdentificationDocumentSchema(t?: TFunction) {
  return z
    .object({
      documentId: z.string().nullable(),
      number: z.string().nullable(),
      expirationDate: z.date().nullable(),
      countryId: z.string().nullable(),
      city: z.string().nullable(),
    })
    .nullable()
    .superRefine((data, ctx) => {
      if (data === null) return
      const valuesToCheckEmptyness = [
        //
        data.documentId,
        data.number,
        data.expirationDate,
        data.countryId,
        data.city,
      ]
      if (valuesToCheckEmptyness.every((x) => x === null || x === '')) return
      if (!data.documentId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('validation.required_error'),
          path: ['documentId'],
        })
      }
      if (!data.number) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('validation.required_error'),
          path: ['number'],
        })
      }
      if (!data.expirationDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('validation.required_error'),
          path: ['expirationDate'],
        })
      } else {
        const today = todayStartOfTheDay()
        if (compareDates(data.expirationDate, today) < 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t?.('pms.booking.reservation.guest.error.expired_document'),
            path: ['expirationDate'],
          })
        }
      }
      if (!data.countryId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('validation.required_error'),
          path: ['countryId'],
        })
      }
    })
}

export function getGuestSchema(t?: TFunction) {
  return z
    .object({
      type: z.nativeEnum(GuestType).nullable(),
      cityTaxExoneration: z.nativeEnum(CityTaxExonerationCode).nullable(),
      start: z.date().nullable(),
      end: z.date().nullable(),
      guestId: z.string().nullable(),
      lastName: z.string().nullable(),
      firstName: z.string().nullable(),
      language: z.enum(Language).nullable(),
      gender: z.nativeEnum(GenderType).nullable(),
      birthDate: z.date().nullable(),
      birthAddress: getGuestAddressSchema(t),
      address: getGuestAddressSchema(t),
      nationalityId: z.string().nullable(),
      identificationDocument: getIdentificationDocumentSchema(t),
      phase: z.nativeEnum(ReservedResourcePhase).nullable(),
      checkin: z.date().nullable(),
      checkout: z.date().nullable(),
    })
    .superRefine((data, ctx) => {
      if (data.phase === ReservedResourcePhase.CHECKIN || data.phase === ReservedResourcePhase.CHECKOUT) {
        if (!data.gender) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t?.('validation.required_error'),
            path: ['gender'],
          })
        }
        if (!data.birthDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t?.('validation.required_error'),
            path: ['birthDate'],
          })
        }
        if (!data.birthAddress) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t?.('validation.required_error'),
            path: ['birthAddress'],
          })
        }
        if (!data.address) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t?.('validation.required_error'),
            path: ['address'],
          })
        }
        if (!data.nationalityId) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t?.('validation.required_error'),
            path: ['nationalityId'],
          })
        }
        if (!data.start) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t?.('validation.required_error'),
            path: ['gender'],
          })
        }
        if (!data.end) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t?.('validation.required_error'),
            path: ['gender'],
          })
        }
        const guarantor = isGuarantor(data.type)
        if (guarantor && !data.identificationDocument) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t?.('validation.required_error'),
            path: ['identificationDocument'],
          })
        }
      }
      const valuesToCheckEmptyness = [
        //
        // data.type,
        // data.cityTaxExoneration,
        // (data.start && !data.end) || (!data.start && data.end) ? data.start ?? data.end : null,
        data.phase,
        data.guestId,
        data.lastName,
        data.firstName,
        data.language,
        data.gender,
        data.birthDate,
        data.birthAddress,
        data.address,
        data.nationalityId,
        data.identificationDocument,
      ]

      if (valuesToCheckEmptyness.every((x) => x === null || x === '' || x === ReservedResourcePhase.RESERVATION)) return
      if (data.type === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('validation.required_error'),
          path: ['type'],
        })
      }
      if (data.guestId === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('validation.required_error'),
          path: ['guestId'],
        })
      }
      if (data.lastName === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('pms.booking.reservation.guest.error.required_last_name'),
          path: ['lastName'],
        })
      }
      if (data.firstName === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('pms.booking.reservation.guest.error.required_first_name'),
          path: ['firstName'],
        })
      }
      if (data.language === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('pms.booking.reservation.guest.error.required_language'),
          path: ['language'],
        })
      }

      if (data.start && !data.end) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('validation.required_error'),
          path: ['end'],
        })
      }

      if (!data.start && data.end) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('validation.required_error'),
          path: ['start'],
        })
      }

      if (data.start && data.end && data.start >= data.end) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('pms.booking.reservation.guest.error.invalid_start_end'),
          path: ['start'],
        })
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('pms.booking.reservation.guest.error.invalid_start_end'),
          path: ['end'],
        })
      }
    })
}

export function getReservedResourceGuestsSchema(t?: TFunction, f?: (date: Date) => string) {
  return z
    .object({
      start: z.date(),
      end: z.date(),
      hotelDate: z.date(),
      guests: getGuestSchema(t).array(),
    })
    .superRefine((data, ctx) => {
      data.guests?.forEach((g, i) => {
        if (g.guestId !== null && g.guestId !== '' && (data.guests.filter((gg) => gg.phase !== ReservedResourcePhase.CHECKOUT && gg.guestId === g.guestId) || []).length >= 2) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t?.('pms.booking.reservation.guest.error.duplicate_guest'),
            path: ['guests', i, 'guestId'],
          })
        }

        if (g.start && g.start < data.start) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t?.('pms.booking.reservation.guest.error.date_not_before', { date: f?.(data.start) ?? data.start.toDateString() }),
            path: ['guests', i, 'start'],
          })
        }

        if (g.end && g.end > data.end) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t?.('pms.booking.reservation.guest.error.date_not_after', { date: f?.(data.end) ?? data.end.toDateString() }),
            path: ['guests', i, 'end'],
          })
        }
      })
    })
}

const reservedResourceGuestsSchema = getReservedResourceGuestsSchema()
export type ReservedResourceGuestsSchema = z.infer<typeof reservedResourceGuestsSchema>
